import { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, ShoppingBag, ChevronLeft, ChevronRight } from "lucide-react";
import { WhatsAppIcon } from "@/components/ui/whatsapp-icon";
import heroMarketplace from "@/assets/hero-marketplace.jpg";
import heroBeautyFashion from "@/assets/hero-beauty-fashion.jpg";
import heroDelivery from "@/assets/hero-delivery.jpg";

const SLIDES = [
  {
    image: heroMarketplace,
    badge: "WhatsApp Commerce Platform",
    badgeIcon: "whatsapp" as const,
    title: "The One-Stop Marketplace for Buyers and Sellers",
    description:
      "Buy everything you need and sell to thousands of customers — all in one place. Share products, receive orders, and get paid seamlessly.",
    primary: { label: "Start Shopping", to: "/products" },
    secondary: { label: "Become a Seller", to: "/register" },
  },
  {
    image: heroBeautyFashion,
    badge: "Style & Living",
    badgeIcon: "bag" as const,
    title: "Shop Beauty, Fashion and Home Decor",
    description:
      "Discover beauty products, fashion finds and home decor from trusted sellers across Nigeria, delivered straight to your doorstep.",
    primary: { label: "Browse Products", to: "/products" },
    secondary: { label: "Start Selling", to: "/register" },
  },
  {
    image: heroDelivery,
    badge: "Pay on Delivery",
    badgeIcon: "bag" as const,
    title: "Order Now, Pay When It Arrives",
    description:
      "Enjoy pay on delivery nationwide. Inspect your order first, then pay the rider or seller on the spot.",
    primary: { label: "Start Shopping", to: "/products" },
    secondary: { label: "Become a Seller", to: "/register" },
  },
];

const SLIDE_INTERVAL = 6000;

export function HeroSlider() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const touchStartX = useRef<number | null>(null);

  const goTo = useCallback((index: number) => {
    setCurrentIndex((index + SLIDES.length) % SLIDES.length);
  }, []);

  useEffect(() => {
    if (isPaused) return;
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % SLIDES.length);
    }, SLIDE_INTERVAL);
    return () => clearInterval(timer);
  }, [isPaused, currentIndex]);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 50) {
      goTo(delta < 0 ? currentIndex + 1 : currentIndex - 1);
    }
    touchStartX.current = null;
  };

  const slide = SLIDES[currentIndex];

  return (
    <section
      className="relative overflow-hidden bg-gradient-subtle"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      aria-roledescription="carousel"
      aria-label="Highlights"
    >
      {/* Background image slides */}
      <div className="absolute inset-0">
        {SLIDES.map((s, i) => (
          <div
            key={s.badge}
            className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
              i === currentIndex ? "opacity-100" : "opacity-0"
            }`}
          >
            <img
              src={s.image}
              alt=""
              className={`w-full h-full object-cover ${i === currentIndex ? "animate-[hero-zoom_6s_ease-out_forwards]" : ""}`}
            />
          </div>
        ))}
        {/* Light overlay so text stays readable on the light theme */}
        <div className="absolute inset-0 bg-background/80" />
        <div className="absolute inset-0 bg-gradient-to-b from-background/60 via-transparent to-background" />
      </div>

      <div className="container mx-auto px-4 py-16 sm:py-20 md:py-28 relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          {/* Per-slide content, re-animated on each change */}
          <div key={currentIndex} className="animate-fade-in">
            <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 bg-whatsapp/10 rounded-full mb-4 sm:mb-6">
              {slide.badgeIcon === "whatsapp" ? (
                <WhatsAppIcon className="h-4 w-4 text-whatsapp" size={16} />
              ) : (
                <ShoppingBag className="h-4 w-4 text-whatsapp" size={16} />
              )}
              <span className="text-xs sm:text-sm font-medium">{slide.badge}</span>
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 sm:mb-6 bg-gradient-primary bg-clip-text text-transparent">
              {slide.title}
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-muted-foreground mb-6 sm:mb-8 max-w-2xl mx-auto px-4 sm:px-0">
              {slide.description}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to={slide.primary.to}>
                <span className="inline-block">
                  <Button size="xl" variant="gradient" className="group">
                    {slide.primary.label}
                    <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </span>
              </Link>
              <Link to={slide.secondary.to}>
                <span className="inline-block">
                  <Button size="xl" variant="glass">
                    <ShoppingBag className="mr-2 h-5 w-5" />
                    {slide.secondary.label}
                  </Button>
                </span>
              </Link>
            </div>
          </div>

          {/* Dots + arrows */}
          <div className="flex items-center justify-center gap-4 mt-8 sm:mt-10">
            <button
              type="button"
              aria-label="Previous slide"
              onClick={() => goTo(currentIndex - 1)}
              className="hidden sm:inline-flex h-9 w-9 items-center justify-center rounded-full bg-background/80 border shadow-sm hover:bg-background transition-colors"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <div className="flex items-center gap-2">
              {SLIDES.map((s, i) => (
                <button
                  key={s.badge}
                  type="button"
                  aria-label={`Go to slide ${i + 1}`}
                  aria-current={i === currentIndex}
                  onClick={() => goTo(i)}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    i === currentIndex
                      ? "w-8 bg-whatsapp"
                      : "w-2 bg-muted-foreground/30 hover:bg-muted-foreground/50"
                  }`}
                />
              ))}
            </div>
            <button
              type="button"
              aria-label="Next slide"
              onClick={() => goTo(currentIndex + 1)}
              className="hidden sm:inline-flex h-9 w-9 items-center justify-center rounded-full bg-background/80 border shadow-sm hover:bg-background transition-colors"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
